import React from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ExternalLink } from "lucide-react";

export function SubscriptionTab() {
  const subscriptionData = useQuery(api.subscriptions.getOrganizationSubscription, {});
  const getPortalUrl = useAction(api.subscriptionsActions.getUserDashboardUrl);

  const handleManageSubscription = async () => {
    if (subscriptionData?.subscription?.customerId) {
      try {
        const result = await getPortalUrl({ customerId: subscriptionData.subscription.customerId });
        if (result?.url) {
          window.open(result.url, "_blank");
        }
      } catch (error) {
        console.error("Failed to open billing portal:", error);
      }
    }
  };

  if (subscriptionData === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const isCanceled = subscriptionData?.subscription?.cancelAtPeriodEnd;
  const planType =
    subscriptionData?.organization?.planType ||
    subscriptionData?.personalWorkspace?.planType ||
    "basic";
  const rawStatus =
    subscriptionData?.organization?.subscriptionStatus ||
    subscriptionData?.personalWorkspace?.subscriptionStatus ||
    "Active";
  const status = planType === "basic" ? "Active" : rawStatus;

  // Formatting helper
  const formatDate = (ts: number | undefined) => {
    if (!ts) return "";
    return new Date(ts * 1000).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Current Plan</CardTitle>
              <CardDescription>Your subscription details</CardDescription>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge
                variant={status === "active" ? "default" : "destructive"}
                className="capitalize"
              >
                {isCanceled ? "Canceled (Pending)" : status || "Active"}
              </Badge>
              {isCanceled && (
                <span className="text-[10px] font-medium text-destructive">
                  Ends {formatDate(subscriptionData?.subscription?.currentPeriodEnd)}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Plan Name and Price */}
          <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {subscriptionData?.planConfig?.name || "Basic"}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {subscriptionData?.planConfig?.description || "Free plan"}
              </p>
              {status === "active" &&
                !isCanceled &&
                subscriptionData?.subscription?.currentPeriodEnd && (
                  <p className="mt-2 text-xs text-gray-500">
                    Next billing date:{" "}
                    {formatDate(subscriptionData?.subscription?.currentPeriodEnd)}
                  </p>
                )}
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-gray-900">
                {subscriptionData?.planConfig?.price === 0
                  ? "Free"
                  : `$${((subscriptionData?.planConfig?.price || 0) / 100).toFixed(0)}`}
              </span>
              {subscriptionData?.planConfig?.price !== 0 && (
                <span className="text-sm text-gray-500">/month</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Manage Subscription</CardTitle>
          <CardDescription>
            Update payment method, view invoices, or cancel subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {subscriptionData?.subscription?.customerId ? (
            <Button onClick={handleManageSubscription} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Open Account Portal
            </Button>
          ) : (
            <Button variant="outline" disabled>
              No active subscription
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
